import { extractSegmentPoints } from "./mapCoordinatesUtils";

/**
 * Compute map center & zoom so all segment coordinates fit.
 *
 * @param {Array} segments - itinerary segments
 * @param {Object} [opts]
 * @param {number} [opts.padding=0.2] - % padding around edges (0.2 = 20%)
 * @param {Array}  [opts.minZoom=[0]] - minimum zoom
 * @param {Array}  [opts.maxZoom=[18]] - maximum zoom
 *
 * @returns {{ center: [number, number], zoom: number }}
 */
export function getViewportForSegments(
  segments = [],
  { padding = 0.2, minZoom = 0, maxZoom = 18 } = {}
) {
  // Collect all valid coordinates
  const points = [];

  for (const seg of segments) {
    const { departure, arrival, single } = extractSegmentPoints(seg);

    if (
      departure &&
      Number.isFinite(departure.lng) &&
      Number.isFinite(departure.lat)
    )
      points.push(departure);
    if (arrival && Number.isFinite(arrival.lng) && Number.isFinite(arrival.lat))
      points.push(arrival);
    if (single && Number.isFinite(single.lng) && Number.isFinite(single.lat))
      points.push(single);
  }

  // No coords — return sane default
  if (!points.length) {
    return {
      center: [-98.5795, 39.8283], // USA center-ish fallback
      zoom: 3,
    };
  }

  // Compute bounding box
  let minLng = Infinity,
    maxLng = -Infinity;
  let minLat = Infinity,
    maxLat = -Infinity;

  for (const p of points) {
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
  }

  // Center
  const center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];

  // Handle the case where all points are the same → zoom max
  if (minLng === maxLng && minLat === maxLat) {
    return {
      center,
      zoom: maxZoom,
    };
  }

  // Determine span
  const lngSpan = maxLng - minLng;
  const latSpan = maxLat - minLat;

  // Apply padding
  const paddedLngSpan = lngSpan * (1 + padding);
  const paddedLatSpan = latSpan * (1 + padding);

  // Rough zoom calc (WebMercator-ish)
  // 360° world / degrees = zoom denominator
  const WORLD = 360;
  const zoomLng = Math.log2(WORLD / paddedLngSpan);
  const zoomLat = Math.log2(WORLD / paddedLatSpan);
  let zoom = Math.min(zoomLng, zoomLat);

  zoom = Math.max(minZoom, Math.min(maxZoom, zoom));

  return { center, zoom };
}

/**
 * Map a map zoom level to a numeric "precision" used for grouping/aggregation.
 *
 * Higher zoom levels return higher precision (less grouping); lower zooms return lower precision (more grouping).
 *
 * Thresholds:
 *  - zoom >= 14 -> 6  (almost full precision — minimal grouping)
 *  - zoom >= 11 -> 4  (city / neighborhood)
 *  - zoom >= 8  -> 3  (region)
 *  - zoom >= 5  -> 2  (country-ish)
 *  - otherwise  -> 1  (coarse world view)
 *
 * @param {number} zoom - Map zoom level (typically non-negative; fractional zoom values are allowed).
 * @returns {number} Precision level as an integer between 1 (coarsest) and 6 (finest).
 *
 * @example
 * // street-level / very zoomed in
 * zoomToPrecision(15); // -> 6
 *
 * @example
 * // city level
 * zoomToPrecision(12); // -> 4
 *
 * @example
 * // world view
 * zoomToPrecision(3); // -> 1
 */
export function zoomToPrecision(zoom) {
  if (zoom >= 14) return 6; // almost full precision → very little grouping
  if (zoom >= 11) return 4; // city / neighborhood
  if (zoom >= 8) return 3; // region
  if (zoom >= 5) return 2; // country-ish
  return 1; // coarse world view
}

// utils/autoPrecision.js

const EARTH_KM_PER_DEG_LAT = 111.32;

/**
 * Compute decimal precision so that one lat/long "step" is roughly
 * targetDistanceKm on the ground.
 *
 * @param {number} targetDistanceKm
 * @returns {number} integer decimal precision [0..6]
 */
function precisionForDistance(targetDistanceKm) {
  if (targetDistanceKm <= 0) return 6;

  const ratio = targetDistanceKm / EARTH_KM_PER_DEG_LAT;
  const p = -Math.log10(ratio);

  // Clamp to [0, 6] and round to nearest int
  return Math.min(6, Math.max(0, Math.round(p)));
}

/**
 * Auto-tune decimal precision from zoom level.
 *
 * You can tweak the mapping of zoom → desired cluster distance to taste.
 *
 * @param {number} zoom
 * @returns {number} decimal precision
 */
export function autoPrecisionForZoom(zoom) {
  let targetDistanceKm;

  if (zoom <= 2) {
    // Very zoomed out: entire continents / world
    // want ~300 km "buckets"
    targetDistanceKm = 300;
  } else if (zoom <= 4) {
    // Country / large region
    targetDistanceKm = 150;
  } else if (zoom <= 6) {
    // State / province
    targetDistanceKm = 60;
  } else if (zoom <= 8) {
    // Metro area / multi-city
    targetDistanceKm = 20;
  } else if (zoom <= 10) {
    // City / airport campus
    targetDistanceKm = 5;
  } else if (zoom <= 12) {
    // Neighborhood / terminal
    targetDistanceKm = 1;
  } else if (zoom <= 14) {
    // Street / building
    targetDistanceKm = 0.2;
  } else {
    // Very close: basically no grouping except exact duplicates
    targetDistanceKm = 0.05;
  }

  return precisionForDistance(targetDistanceKm);
}
